import {
  getDataFromTable,
  getProfile,
  getUserProfileWithLinkedData,
  getEnrolledCourses,
  getUserActivity,
} from "./services";

export interface AdminEntity {
  id: string;
  profilePic: string;
  name: string;
  email: string;
  contact: string;
  feedback?: string;
  subject?: string;
  joiningDate: string;
  verified: boolean;
  joinedAt: number;
}

export interface AdminCourseGroup {
  teacherId: number;
  name: string;
  email: string;
  phone: string;
  isVerified: boolean;
  courses: {
    id: number;
    image: string;
    title: string;
    duration: string;
  }[];
}

export interface FeedbackItem {
  id: string;
  name: string;
  message: string;
  time: string;
}

export interface BillingStat {
  feetype: string;
  totalAmount: number;
  percentageIncrease: number;
}

export interface ChildProfile {
  id: number;
  name: string;
  pfp: string;
}

export interface ChildProgressCourse {
  id: string;
  title: string;
  progress: number;
}

const extractList = (res: any): any[] => res?.data?.data ?? [];

const formatDate = (value?: string) => {
  if (!value) return "N/A";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatTime = (value?: string) => {
  if (!value) return "";
  return new Date(value).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
};

const isSuccessfulPayment = (status?: string) =>
  status === "success" || status === "completed";

export const parseReviewFeedback = (feedback?: string) => {
  let name = "Student";
  let text = feedback || "";
  try {
    if (feedback) {
      const parsed = JSON.parse(feedback);
      if (parsed && typeof parsed === "object") {
        name = parsed.authorName || "Student";
        text = parsed.text || "";
      }
    }
  } catch {
    // plain text feedback
  }
  return { name, text };
};

const daysAgo = (days: number) => Date.now() - days * 24 * 60 * 60 * 1000;

async function fetchCoreRecords() {
  const [
    coursesRes,
    enrollmentsRes,
    paymentsRes,
    ratingsRes,
    skillsRes,
    userSkillsRes,
    addressesRes,
    notificationsRes,
    supportRes,
  ] = await Promise.all([
    getDataFromTable("course"),
    getDataFromTable("enrolledCourse"),
    getDataFromTable("payments"),
    getDataFromTable("userRatings"),
    getDataFromTable("skills"),
    getDataFromTable("userSkills"),
    getDataFromTable("address"),
    getDataFromTable("notifications"),
    getDataFromTable("support"),
  ]);

  return {
    courses: extractList(coursesRes),
    enrollments: extractList(enrollmentsRes),
    payments: extractList(paymentsRes),
    ratings: extractList(ratingsRes),
    skills: extractList(skillsRes),
    userSkills: extractList(userSkillsRes),
    addresses: extractList(addressesRes),
    notifications: extractList(notificationsRes),
    supportTickets: extractList(supportRes),
  };
}

function buildUserNameMap(ratings: any[], courses: any[]) {
  const map = new Map<number, string>();
  ratings.forEach((rating) => {
    const { name } = parseReviewFeedback(rating.feedback);
    if (rating.userId && name !== "Student") {
      map.set(Number(rating.userId), name);
    }
  });
  courses.forEach((course) => {
    if (course.createdBy && course.teachersName) {
      map.set(Number(course.createdBy), course.teachersName);
    }
  });
  return map;
}

function buildAddressMap(addresses: any[]) {
  const map = new Map<number, { contact: string; email: string }>();
  addresses.forEach((address) => {
    if (!address.userId) return;
    map.set(Number(address.userId), {
      contact: address.city || address.postalCode || "N/A",
      email: "N/A",
    });
  });
  return map;
}

function buildStudentEntities(
  enrollments: any[],
  payments: any[],
  ratings: any[],
  addresses: any[],
): AdminEntity[] {
  const nameMap = buildUserNameMap(ratings, []);
  const addressMap = buildAddressMap(addresses);
  const verifiedUserIds = new Set<number>();
  const userDates = new Map<number, number>();

  payments.forEach((payment) => {
    if (!payment.userId || !isSuccessfulPayment(payment.status)) return;
    verifiedUserIds.add(Number(payment.userId));
    const ts = payment.createdAt
      ? new Date(payment.createdAt).getTime()
      : Date.now();
    const existing = userDates.get(Number(payment.userId));
    if (!existing || ts < existing) {
      userDates.set(Number(payment.userId), ts);
    }
  });

  enrollments.forEach((enrollment) => {
    if (!enrollment.userId) return;
    const ts = enrollment.createdAt
      ? new Date(enrollment.createdAt).getTime()
      : Date.now();
    const existing = userDates.get(Number(enrollment.userId));
    if (!existing || ts < existing) {
      userDates.set(Number(enrollment.userId), ts);
    }
  });

  return Array.from(userDates.entries()).map(([userId, joinedAt]) => {
    const addressInfo = addressMap.get(userId);
    return {
      id: String(userId),
      profilePic: "/assets/pfp.png",
      name: nameMap.get(userId) || `Student #${userId}`,
      email: addressInfo?.email || "N/A",
      contact: addressInfo?.contact || "N/A",
      joiningDate: formatDate(new Date(joinedAt).toISOString()),
      verified: verifiedUserIds.has(userId),
      joinedAt,
    };
  });
}

function buildTeacherEntities(courses: any[], addresses: any[]): AdminEntity[] {
  const addressMap = buildAddressMap(addresses);
  const teacherMap = new Map<number, AdminEntity>();

  courses.forEach((course) => {
    const teacherId = Number(course.createdBy);
    if (!teacherId) return;

    const joinedAt = course.createdAt
      ? new Date(course.createdAt).getTime()
      : Date.now();
    const existing = teacherMap.get(teacherId);
    const addressInfo = addressMap.get(teacherId);
    const isPublished = course.status === "published";

    if (!existing) {
      teacherMap.set(teacherId, {
        id: String(teacherId),
        profilePic: "/assets/pfp.png",
        name: course.teachersName || `Teacher #${teacherId}`,
        email: addressInfo?.email || "N/A",
        contact: addressInfo?.contact || "N/A",
        joiningDate: formatDate(course.createdAt),
        verified: isPublished,
        joinedAt,
      });
      return;
    }

    if (joinedAt < existing.joinedAt) {
      existing.joinedAt = joinedAt;
      existing.joiningDate = formatDate(course.createdAt);
    }
    if (isPublished) existing.verified = true;
    if (course.teachersName) existing.name = course.teachersName;
  });

  return Array.from(teacherMap.values());
}

export function filterEntitiesByTab(
  entities: AdminEntity[],
  tab: string,
): AdminEntity[] {
  const now = Date.now();
  if (tab === "New Joined") {
    return entities.filter((entity) => entity.joinedAt >= daysAgo(30));
  }
  if (tab === "Verified") {
    return entities.filter((entity) => entity.verified);
  }
  if (tab === "Archived") {
    return entities.filter(
      (entity) => !entity.verified && entity.joinedAt < daysAgo(90),
    );
  }
  return entities;
}

export function countEntitiesByTab(entities: AdminEntity[]) {
  return {
    "New Joined": filterEntitiesByTab(entities, "New Joined").length,
    Verified: filterEntitiesByTab(entities, "Verified").length,
    Archived: filterEntitiesByTab(entities, "Archived").length,
  };
}

export async function fetchStudents() {
  const { enrollments, payments, ratings, addresses } =
    await fetchCoreRecords();
  return buildStudentEntities(enrollments, payments, ratings, addresses);
}

export async function fetchTeachers() {
  const { courses, addresses } = await fetchCoreRecords();
  return buildTeacherEntities(courses, addresses);
}

export async function fetchAdminHomeData(selectedMonth: Date) {
  const {
    courses,
    enrollments,
    payments,
    ratings,
    skills,
    userSkills,
    notifications,
  } = await fetchCoreRecords();

  const monthStart = new Date(
    selectedMonth.getFullYear(),
    selectedMonth.getMonth(),
    1,
  );
  const monthEnd = new Date(
    selectedMonth.getFullYear(),
    selectedMonth.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  );

  const monthEnrollments = enrollments.filter((item) => {
    const date = new Date(item.createdAt);
    return date >= monthStart && date <= monthEnd;
  });

  const monthPayments = payments.filter((item) => {
    if (!isSuccessfulPayment(item.status)) return false;
    const date = new Date(item.createdAt);
    return date >= monthStart && date <= monthEnd;
  });

  const enrollmentsByCourse = new Map<number, number>();
  monthEnrollments.forEach((item) => {
    const courseId = Number(item.courseId);
    enrollmentsByCourse.set(
      courseId,
      (enrollmentsByCourse.get(courseId) || 0) + 1,
    );
  });

  const topCourses = courses
    .map((course) => ({
      id: Number(course.id),
      title: course.title || `Course #${course.id}`,
      count: enrollmentsByCourse.get(Number(course.id)) || 0,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const avgEnrollment =
    topCourses.length > 0
      ? topCourses.reduce((sum, course) => sum + course.count, 0) /
        topCourses.length
      : 0;

  const studentData = topCourses.map((course) => ({
    name: course.title.slice(0, 12),
    uv: course.count,
    avg: Math.round(avgEnrollment),
  }));

  const skillCounts = new Map<string, number>();
  userSkills.forEach((item) => {
    const title =
      item.skill?.title ||
      skills.find((skill) => skill.id === item.skillId)?.title ||
      `Skill #${item.skillId}`;
    skillCounts.set(title, (skillCounts.get(title) || 0) + 1);
  });

  const maxSkillCount = Math.max(...Array.from(skillCounts.values()), 1);
  const skillsData = Array.from(skillCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([skillName, registered]) => ({
      skillName,
      registered,
      percentage: Math.round((registered / maxSkillCount) * 100),
    }));

  const courseVisibility = courses
    .slice(0, 5)
    .map((course) => ({
      name: (course.title || "Course").slice(0, 10),
      visibility: enrollments.filter(
        (item) => Number(item.courseId) === Number(course.id),
      ).length,
    }));

  const teacherRatingMap = new Map<string, { total: number; count: number }>();
  ratings.forEach((rating) => {
    const course = courses.find(
      (item) => `course_${item.id}` === rating.feedbackType,
    );
    const teacherName = course?.teachersName || "Teacher";
    const current = teacherRatingMap.get(teacherName) || { total: 0, count: 0 };
    current.total += Number(rating.rating) || 0;
    current.count += 1;
    teacherRatingMap.set(teacherName, current);
  });

  const teacherRatings = Array.from(teacherRatingMap.entries())
    .map(([name, stats]) => ({
      name,
      score: stats.count
        ? Math.round((stats.total / stats.count / 5) * 100)
        : 0,
    }))
    .slice(0, 6);

  const unverifiedCourses = courses
    .filter((course) => course.status !== "published")
    .slice(0, 5)
    .map((course) => ({
      title: course.title || "Untitled Course",
      date: formatDate(course.updatedAt || course.createdAt),
      time: formatTime(course.updatedAt || course.createdAt),
    }));

  const feedbackItems: FeedbackItem[] = [
    ...notifications.slice(0, 3).map((item) => ({
      id: String(item.id),
      name: item.title || "Notification",
      message: item.message || "",
      time: `${formatDate(item.createdAt)} ${formatTime(item.createdAt)}`.trim(),
    })),
    ...ratings.slice(0, 3).map((item) => {
      const { name, text } = parseReviewFeedback(item.feedback);
      return {
        id: `rating-${item.id}`,
        name,
        message: text || "Left a course review",
        time: `${formatDate(item.createdAt)} ${formatTime(item.createdAt)}`.trim(),
      };
    }),
  ].slice(0, 5);

  const newStudentsCount = enrollments.filter(
    (item) =>
      item.createdAt && new Date(item.createdAt).getTime() >= daysAgo(30),
  ).length;

  const totalRevenue = monthPayments.reduce(
    (sum, payment) => sum + (Number(payment.amount) || 0),
    0,
  );

  return {
    studentData,
    skillsData,
    courseData: courseVisibility,
    ratings: teacherRatings,
    unverifiedCourses,
    feedbackItems,
    newStudentsCount,
    totalRevenue,
    periodLabel: selectedMonth.toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    }),
  };
}

export async function fetchAdminCoursesGrouped(): Promise<AdminCourseGroup[]> {
  const { courses, addresses } = await fetchCoreRecords();
  const addressMap = buildAddressMap(addresses);
  const groups = new Map<number, AdminCourseGroup>();

  courses.forEach((course) => {
    const teacherId = Number(course.createdBy);
    if (!teacherId) return;

    const addressInfo = addressMap.get(teacherId);
    if (!groups.has(teacherId)) {
      groups.set(teacherId, {
        teacherId,
        name: course.teachersName || `Teacher #${teacherId}`,
        email: addressInfo?.email || "N/A",
        phone: addressInfo?.contact || "N/A",
        isVerified: course.status === "published",
        courses: [],
      });
    }

    const group = groups.get(teacherId)!;
    if (course.status === "published") group.isVerified = true;
    if (course.teachersName) group.name = course.teachersName;
    group.courses.push({
      id: Number(course.id),
      image: course.coverImage || "/assets/Video-img.png",
      title: course.title || "Untitled Course",
      duration: course.timeline || "N/A",
    });
  });

  return Array.from(groups.values());
}

export async function fetchAdminFeedback(): Promise<AdminEntity[]> {
  const { ratings, supportTickets, courses } = await fetchCoreRecords();

  const feedbackFromRatings = ratings.map((item) => {
    const { name, text } = parseReviewFeedback(item.feedback);
    const course = courses.find(
      (courseItem) => `course_${courseItem.id}` === item.feedbackType,
    );
    return {
      id: String(item.id),
      profilePic: "/assets/pfp.png",
      name,
      feedback: text || "No comment provided.",
      subject: course?.title || "Course Review",
      joiningDate: formatDate(item.createdAt),
      verified: Number(item.rating) >= 4,
      joinedAt: item.createdAt
        ? new Date(item.createdAt).getTime()
        : Date.now(),
    } as AdminEntity;
  });

 const feedbackFromSupport = supportTickets.map(
  (item) =>
    ({
      id: `support-${item.id}`,
      profilePic: "/assets/pfp.png",
      name: item.userType || `User #${item.userId}`,
      feedback: item.queryDesc || item.query || "Support request",
      subject: item.queryType || "Support",
      joiningDate: formatDate(item.createdAt),
      verified: item.status === "resolved",
      joinedAt: item.createdAt
        ? new Date(item.createdAt).getTime()
        : Date.now(),
    }) as AdminEntity,
);

  return [...feedbackFromRatings, ...feedbackFromSupport].sort(
    (a, b) => b.joinedAt - a.joinedAt,
  );
}

function calcPercentChange(current: number, previous: number) {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

export async function fetchBillingStats(): Promise<BillingStat[]> {
  const { payments } = await fetchCoreRecords();
  const successful = payments.filter((payment) =>
    isSuccessfulPayment(payment.status),
  );

  const now = Date.now();
  const totalAmount = successful.reduce(
    (sum, payment) => sum + (Number(payment.amount) || 0),
    0,
  );

  const last30 = successful.filter(
    (payment) =>
      payment.createdAt &&
      new Date(payment.createdAt).getTime() >= daysAgo(30),
  );
  const prev30 = successful.filter((payment) => {
    if (!payment.createdAt) return false;
    const ts = new Date(payment.createdAt).getTime();
    return ts >= daysAgo(60) && ts < daysAgo(30);
  });

  const last7 = successful.filter(
    (payment) =>
      payment.createdAt &&
      new Date(payment.createdAt).getTime() >= daysAgo(7),
  );
  const prev7 = successful.filter((payment) => {
    if (!payment.createdAt) return false;
    const ts = new Date(payment.createdAt).getTime();
    return ts >= daysAgo(14) && ts < daysAgo(7);
  });

  const monthlyAmount = last30.reduce(
    (sum, payment) => sum + (Number(payment.amount) || 0),
    0,
  );
  const prevMonthlyAmount = prev30.reduce(
    (sum, payment) => sum + (Number(payment.amount) || 0),
    0,
  );
  const weeklyAmount = last7.reduce(
    (sum, payment) => sum + (Number(payment.amount) || 0),
    0,
  );
  const prevWeeklyAmount = prev7.reduce(
    (sum, payment) => sum + (Number(payment.amount) || 0),
    0,
  );

  const totalPrev = successful
    .filter((payment) => {
      if (!payment.createdAt) return false;
      return new Date(payment.createdAt).getTime() < daysAgo(30);
    })
    .reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);

  return [
    {
      feetype: "Total",
      totalAmount,
      percentageIncrease: calcPercentChange(totalAmount, totalPrev),
    },
    {
      feetype: "Monthly",
      totalAmount: monthlyAmount,
      percentageIncrease: calcPercentChange(monthlyAmount, prevMonthlyAmount),
    },
    {
      feetype: "Weekly",
      totalAmount: weeklyAmount,
      percentageIncrease: calcPercentChange(weeklyAmount, prevWeeklyAmount),
    },
  ];
}

export async function fetchParentDashboard(parentId: number) {
  let parentName = "Parent";
  let children: ChildProfile[] = [];

  try {
    const profileRes = await getProfile();
    const profile = profileRes?.data?.data ?? {};
    parentName = profile.name || parentName;
    const linked =
      profile.children ||
      profile.childUsers ||
      profile.linkedChildren ||
      profile.students;
    if (Array.isArray(linked)) {
      children = linked.map((child: any, index: number) => ({
        id: Number(child.id ?? child.userId ?? index),
        name: child.name || `Child ${index + 1}`,
        pfp: child.userImage || child.profilePic || "/assets/pfp.png",
      }));
    }
  } catch {
    // ignore profile errors
  }

  if (children.length === 0) {
    try {
      const linkedRes = await getUserProfileWithLinkedData({ userId: parentId });
      const linkedData = linkedRes?.data?.data ?? linkedRes?.data ?? {};
      const linked =
        linkedData.children ||
        linkedData.childUsers ||
        linkedData.linkedChildren ||
        linkedData.students;
      if (Array.isArray(linked)) {
        children = linked.map((child: any, index: number) => ({
          id: Number(child.id ?? child.userId ?? index),
          name: child.name || `Child ${index + 1}`,
          pfp: child.userImage || child.profilePic || "/assets/pfp.png",
        }));
      }
    } catch {
      // endpoint may not exist yet
    }
  }

  if (children.length === 0) {
    const { enrollments, ratings } = await fetchCoreRecords();
    const childIds = new Set<number>();
    enrollments.forEach((item) => {
      if (item.userId) childIds.add(Number(item.userId));
    });

    const nameMap = buildUserNameMap(ratings, []);
    children = Array.from(childIds).map((childId) => ({
      id: childId,
      name: nameMap.get(childId) || `Student #${childId}`,
      pfp: "/assets/pfp.png",
    }));
  }

  return { parentName, children };
}

export async function fetchChildProgress(
  childId: number,
  selectedMonth: Date,
  childName?: string,
) {
  const [enrolledRes, activityRes, coursesRes] = await Promise.all([
    getEnrolledCourses(childId),
    getUserActivity(childId),
    getDataFromTable("course"),
  ]);

  const enrollments = extractList(enrolledRes);
  const activities = extractList(activityRes);
  const courses = extractList(coursesRes);
  const courseMap = new Map<number, any>(
    courses.map((course) => [Number(course.id), course]),
  );

  const monthStart = new Date(
    selectedMonth.getFullYear(),
    selectedMonth.getMonth(),
    1,
  );
  const monthEnd = new Date(
    selectedMonth.getFullYear(),
    selectedMonth.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  );

  const progressCourses: ChildProgressCourse[] = await Promise.all(
    enrollments.map(async (enrollment) => {
      const course = courseMap.get(Number(enrollment.courseId));
      return {
        id: String(enrollment.courseId),
        title: course?.title || `Course #${enrollment.courseId}`,
        progress: Number(enrollment.progress) || 0,
      };
    }),
  );

  const monthActivities = activities.filter((activity) => {
    if (!activity.createdAt) return false;
    const date = new Date(activity.createdAt);
    return date >= monthStart && date <= monthEnd;
  });

  const activityByCourse = new Map<string, number>();
  monthActivities.forEach((activity) => {
    const label = activity.activityType || "General";
    activityByCourse.set(label, (activityByCourse.get(label) || 0) + 1);
  });

  if (activityByCourse.size === 0) {
    progressCourses.forEach((course) => {
      activityByCourse.set(course.title, course.progress);
    });
  }

  const graphData = Array.from(activityByCourse.entries())
    .slice(0, 4)
    .map(([name, value]) => ({ name: name.slice(0, 12), value }));

  const weeksInMonth = Math.ceil(
    (monthEnd.getDate() - monthStart.getDate() + 1) / 7,
  );
  const weeksData = Array.from(
    { length: Math.max(weeksInMonth, 1) },
    (_, index) => `Week ${index + 1}`,
  );

  const resolvedChildName = childName || `Student #${childId}`;

  return {
    childName: resolvedChildName,
    progressCourses,
    graphData,
    weeksData,
    labels: graphData.map((item) => item.name),
    periodLabel: selectedMonth.toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    }),
  };
}

export function shiftMonth(date: Date, direction: "previous" | "next") {
  const next = new Date(date);
  next.setMonth(next.getMonth() + (direction === "next" ? 1 : -1));
  return next;
}
